/**
 * NAVIGERINGEN. Historik-API:et och ingenting mer — inget routerbibliotek.
 *
 * VARFÖR EGEN. Appen har tolv skärmar, en bakåtknapp som följer en
 * förälderkedja och en regel om vilka skärmar som över huvud taget finns för
 * den inloggade. Det är ~90 rader. Ett bibliotek hade lagt till ett andra
 * ställe där skärmar registreras, och det är precis det som får fyra parallella
 * agenter att bygga varsin variant.
 *
 * MATCHNINGEN ÄR SEGMENTVIS, samma grepp som worker/src/router/route-table.ts:
 * sökvägen delas på '/', ett segment som börjar på ':' är en parameter och
 * avkodas EFTER delningen. Ett reguljärt uttryck byggt av strängar hade brutit
 * på första arbetsorder-id som innehåller ett bindestreck.
 *
 * SKÄRMAR SOM INTE FINNS FÖR DIG FINNS INTE, PUNKT. Routern söker bara bland
 * `synligaSkarmar(kanSeLaget)`. En montör som skriver /attestera i adressfältet
 * får inte 403 och inte en tom sida — han hamnar på startsidan, för adressen
 * matchar ingenting i HANS karta. Det är samma regel som gör lägesväxeln
 * obefintlig i stället för gömd, och den gäller även vid en direktinladdning
 * från en bokmärkt adress.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { STARTSKARM, skarmMedId, synligaSkarmar } from './skarmar';
import type { Skarm, SkarmId } from './skarmar';

export type Sokvagsparametrar = Readonly<Record<string, string>>;

export interface Plats {
  readonly skarm: Skarm;
  readonly params: Sokvagsparametrar;
}

export interface Navigering {
  readonly plats: Plats;
  /** Gå till en skärm. Parametrarna måste täcka varje ':'-segment i sökvägen. */
  readonly ga: (id: SkarmId, params?: Sokvagsparametrar) => void;
  /** Ett steg upp i förälderkedjan. På en startsida händer ingenting. */
  readonly tillbaka: () => void;
  /** Ersätter adressen utan att lägga en post i historiken. */
  readonly ersatt: (id: SkarmId, params?: Sokvagsparametrar) => void;
}

const delar = (sokvag: string): readonly string[] => {
  const rensad = sokvag.length > 1 && sokvag.endsWith('/') ? sokvag.slice(0, -1) : sokvag;
  return rensad.split('/').slice(1);
};

export const byggAdress = (monster: string, params: Sokvagsparametrar): string => {
  const bitar = delar(monster).map((del) => {
    if (!del.startsWith(':')) return del;
    const varde = params[del.slice(1)];
    if (varde === undefined || varde === '') {
      throw new Error(`sökvägsparametern ${del.slice(1)} saknas för ${monster}`);
    }
    return encodeURIComponent(varde);
  });
  return `/${bitar.join('/')}`;
};

const passa = (skarm: Skarm, bitar: readonly string[]): Sokvagsparametrar | null => {
  const monster = delar(skarm.sokvag);
  if (monster.length !== bitar.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < monster.length; i += 1) {
    const segment = monster[i];
    const bit = bitar[i];
    if (segment === undefined || bit === undefined) return null;
    if (!segment.startsWith(':')) {
      if (segment !== bit) return null;
      continue;
    }
    if (bit === '') return null;
    try {
      params[segment.slice(1)] = decodeURIComponent(bit);
    } catch {
      return null;
    }
  }
  return params;
};

/** Adress -> plats, eller null när ingen SYNLIG skärm matchar. */
export const traffa = (sokvag: string, kanSeLaget: boolean): Plats | null => {
  const bitar = delar(sokvag);
  for (const skarm of synligaSkarmar(kanSeLaget)) {
    const params = passa(skarm, bitar);
    if (params !== null) return { skarm, params };
  }
  return null;
};

const NavigeringsSammanhang = createContext<Navigering | null>(null);

export const Navigeringsvard = ({
  kanSeLaget,
  children,
}: {
  readonly kanSeLaget: boolean;
  readonly children: ReactNode;
}) => {
  const [sokvag, sattSokvag] = useState(() => globalThis.location.pathname);

  useEffect(() => {
    const vidPop = (): void => sattSokvag(globalThis.location.pathname);
    globalThis.addEventListener('popstate', vidPop);
    return () => globalThis.removeEventListener('popstate', vidPop);
  }, []);

  const traffad = traffa(sokvag, kanSeLaget);

  /**
   * OKÄND ELLER OÅTKOMLIG ADRESS -> startsidan, med replaceState så att
   * bakåtknappen inte studsar tillbaka till den. Det är samma väg för en
   * felstavad adress och för /attestera hos en montör, och det är meningen:
   * han ska inte kunna sluta sig till att skärmen finns.
   */
  const plats: Plats = traffad ?? { skarm: skarmMedId(STARTSKARM.hem), params: {} };

  useEffect(() => {
    if (traffad === null && globalThis.location.pathname !== plats.skarm.sokvag) {
      globalThis.history.replaceState(null, '', plats.skarm.sokvag);
      sattSokvag(plats.skarm.sokvag);
    }
  }, [traffad, plats.skarm.sokvag]);

  const byt = useCallback(
    (id: SkarmId, params: Sokvagsparametrar, ersattning: boolean): void => {
      const adress = byggAdress(skarmMedId(id).sokvag, params);
      if (ersattning) globalThis.history.replaceState(null, '', adress);
      else globalThis.history.pushState(null, '', adress);
      sattSokvag(adress);
      // Ny skärm börjar högst upp. Prototypen gör samma sak (phone-body.scrollTop).
      globalThis.scrollTo({ top: 0 });
    },
    [],
  );

  const varde = useMemo<Navigering>(
    () => ({
      plats,
      ga: (id, params = {}) => byt(id, params, false),
      ersatt: (id, params = {}) => byt(id, params, true),
      tillbaka: () => {
        const forald = plats.skarm.foralder;
        if (forald === null) return;
        // history.back() vore fel: bakåtknappen i appen följer FÖRÄLDERKEDJAN,
        // inte besöksordningen. Den som kom till 'tidny' från tidskärmen ska
        // ändå hamna på arbetsordern, precis som prototypen gör.
        byt(forald, plats.params, false);
      },
    }),
    [plats, byt],
  );

  return <NavigeringsSammanhang value={varde}>{children}</NavigeringsSammanhang>;
};

/**
 * NAVIGERINGEN OM DEN FINNS — null utanför <Navigeringsvard>.
 *
 * ANVÄND useNavigering NEDAN I VARJE SKÄRM. Kastet där är rätt: en skärm som
 * renderats utanför sin värd ska falla högljutt och inte tyst sluta navigera.
 * Undantaget är skärmen som lägger upp firman, som med flit står på två ställen
 * — som registrerad skärm i appen OCH i världen 'ingen_firma', som ligger
 * utanför navigeringen (App.tsx). Den, och bara den, frågar med den här.
 */
export const useNavigeringOm = (): Navigering | null => useContext(NavigeringsSammanhang);

export const useNavigering = (): Navigering => {
  const varde = useNavigeringOm();
  if (varde === null) {
    throw new Error('useNavigering användes utanför <Navigeringsvard>');
  }
  return varde;
};

/** Genväg: bara den aktuella platsen. */
export const usePlats = (): Plats => useNavigering().plats;
